import { ChevronUp, Facebook, Instagram, Mail, Phone, Users, Youtube } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi, { buildPhoneHref, getDisplayPhoneNumber } from "../services/odooApi";

// TikTok Icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Telegram Icon
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [config, setConfig] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [visitorCount, setVisitorCount] = useState(0);
  const phoneLabel = getDisplayPhoneNumber(config?.phone);
  const phoneHref = buildPhoneHref(config?.phone);

  const handlePhoneClick = () => {
    odooApi.trackClick({ click_type: "call" });
  };

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const [cats, cts, brs, cfg] = await Promise.all([
          odooApi.getCategories(),
          odooApi.getCities(),
          odooApi.getBrands(),
          odooApi.getCmsConfig(),
        ]);
        setCategories(cats);
        setCities(cts);
        setBrands(brs.slice(0, 6));
        setConfig(cfg);
      } catch (err) {
        console.error("Failed to load footer data:", err);
      }
    };
    fetchFooterData();

    // Simulated visitor counter (in production, this would be from analytics)
    const storedCount = localStorage.getItem('velaris_visitor_count');
    const count = storedCount ? parseInt(storedCount) : Math.floor(Math.random() * 50000) + 100000;
    setVisitorCount(count);
    localStorage.setItem('velaris_visitor_count', (count + 1).toString());

    // Back to top button visibility
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: t("nav.about", "About Us"), path: "/about" },
    { name: t("nav.contact", "Contact"), path: "/contact" },
    { name: t("nav.faq", "FAQ"), path: "/faq" },
    { name: t("common.support", "Support"), path: "/support" },
    { name: t("footer.privacyPolicy", "Privacy Policy"), path: "/privacy-policy" },
    { name: t("nav.pricing", "Pricing"), path: "/pricing" },
  ];

  return (
    <>
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 z-40 w-12 h-12 bg-[#D4AF37] hover:bg-[#C5A028] text-black rounded-full shadow-lg flex items-center justify-center transition-all transform hover:scale-110"
          data-testid="back-to-top"
          aria-label={t("footer.backToTop", "Back to top")}
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      <footer className="bg-black text-white" data-testid="footer">
        {/* Location Map Section */}
        <div className="border-b border-[#D4AF37]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Map */}
              <div className="aspect-video lg:aspect-[2/1] rounded-xl overflow-hidden border border-[#D4AF37]/30">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d462560.6828124055!2d54.947286815820544!3d25.075384839671!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43496ad9c645%3A0xbde66e5084295162!2sDubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2s!4v1704000000000!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t("accessibility.locationMap", "Velaris Royal Location")}
                />
              </div>
              
              {/* Contact Info */}
              <div className="space-y-6">
                <div>
                  <h3 className="font-[var(--font-display)] text-2xl font-bold text-white mb-2">
                    {t("footer.visitUs", "Visit Our Office")}
                  </h3>
                  <p className="text-white/60">
                    {config?.address || 'Business Bay, Dubai, UAE'}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {phoneHref !== '#' && (
                    <a
                      href={phoneHref}
                      onClick={handlePhoneClick}
                      className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-[#D4AF37]/10 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
                        <Phone className="w-5 h-5 text-black" />
                      </div>
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider">{t("footer.callUs", "Call Us")}</p>
                        <p className="text-white font-medium">{phoneLabel}</p>
                      </div>
                    </a>
                  )}
                  <a 
                    href={`mailto:${config?.email || 'info@velaris.ae'}`}
                    className="flex items-center gap-3 p-4 bg-white/5 rounded-lg hover:bg-[#D4AF37]/10 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wider">{t("footer.emailUs", "Email Us")}</p>
                      <p className="text-white font-medium">{config?.email || 'info@velaris.ae'}</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Brand Column - Enlarged Logo */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-6">
                <img
                  src="/velaris-brand-mark-attached.png"
                  alt={t("accessibility.logoAlt", "Velaris Royal Car Rental")}
                  className="h-20 w-auto object-contain"
                />
              </Link>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                {config?.footer_about || t(
                  "footer.aboutFallback",
                  "Premium car rental services in the UAE. Experience luxury and comfort with our extensive fleet of vehicles.",
                )}
              </p>
              
              {/* Social Media Icons */}
              <div className="flex flex-wrap gap-3">
                <a href={config?.social?.facebook || 'https://facebook.com'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center hover:bg-[#1877F2] hover:border-[#1877F2] transition-all" data-testid="social-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href={config?.social?.instagram || 'https://instagram.com'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 hover:border-transparent transition-all" data-testid="social-instagram">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={config?.social?.tiktok || 'https://tiktok.com'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center hover:bg-white hover:text-black hover:border-white transition-all" data-testid="social-tiktok">
                  <TikTokIcon className="w-4 h-4" />
                </a>
                <a href={config?.social?.telegram || 'https://t.me'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center hover:bg-[#0088cc] hover:border-[#0088cc] transition-all" data-testid="social-telegram">
                  <TelegramIcon className="w-4 h-4" />
                </a>
                <a href={config?.social?.youtube || 'https://youtube.com'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center hover:bg-[#FF0000] hover:border-[#FF0000] transition-all" data-testid="social-youtube">
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Categories */}
            <div>
              <h4 className="font-[var(--font-display)] text-lg font-semibold mb-6 text-[#D4AF37]">
                {t("footer.categories", "Categories")}
              </h4>
              <ul className="space-y-3">
                {categories.map((cat) => (
                  <li key={cat.slug}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="footer-link text-white/60 text-sm hover:text-[#D4AF37] transition-all"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cities */}
            <div>
              <h4 className="font-[var(--font-display)] text-lg font-semibold mb-6 text-[#D4AF37]">
                {t("footer.cities", "Locations")}
              </h4>
              <ul className="space-y-3">
                {cities.map((city) => (
                  <li key={city.slug}>
                    <Link
                      to={`/city/${city.slug}`}
                      className="footer-link text-white/60 text-sm hover:text-[#D4AF37] transition-all"
                    >
                      {city.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Brands */}
            <div>
              <h4 className="font-[var(--font-display)] text-lg font-semibold mb-6 text-[#D4AF37]">
                {t("footer.brands", "Top Brands")}
              </h4>
              <ul className="space-y-3">
                {brands.map((brand) => (
                  <li key={brand.slug}>
                    <Link
                      to={`/brand/${brand.slug}`}
                      className="footer-link text-white/60 text-sm hover:text-[#D4AF37] transition-all"
                    >
                      {brand.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links & App Downloads */}
            <div>
              <h4 className="font-[var(--font-display)] text-lg font-semibold mb-6 text-[#D4AF37]">
                {t("footer.quickLinks", "Quick Links")}
              </h4>
              <ul className="space-y-3 mb-8">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="footer-link text-white/60 text-sm hover:text-[#D4AF37] transition-all"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* App Download Buttons */}
              <h5 className="text-sm font-semibold mb-3 text-white/80">{t("footer.downloadApp", "Download App")}</h5>
              <div className="flex flex-col gap-2">
                <a href="#" className="inline-block">
                  <img
                    src="/store-badges/google-play-badge.svg"
                    alt={t("footer.googlePlayAlt", "Get it on Google Play")}
                    className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
                <a href="#" className="inline-block">
                  <img
                    src="/store-badges/app-store-badge.svg"
                    alt={t("footer.appStoreAlt", "Download on App Store")}
                    className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#D4AF37]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              {/* Copyright */}
              <div className="text-center md:text-left">
                <p className="text-white/40 text-sm">
                  &copy; {currentYear} {config?.copyright_text || 'Velaris Royal Car Rental. All rights reserved.'}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/30">
                  Powered by PERLA TECH
                </p>
              </div>

              {/* Visitor Counter */}
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <Users className="w-4 h-4 text-[#D4AF37]" />
                <span>{visitorCount.toLocaleString()} {t("footer.visitors", "visitors")}</span>
              </div>

              {/* Payment Methods */}
              <div className="flex items-center gap-4">
                <span className="text-white/40 text-xs">{t("footer.weAccept", "We Accept")}:</span>
                <div className="flex items-center gap-2">
                  <div className="bg-white/10 rounded px-2 py-1">
                    <span className="text-xs font-bold text-white/60">VISA</span>
                  </div>
                  <div className="bg-white/10 rounded px-2 py-1">
                    <span className="text-xs font-bold text-white/60">MC</span>
                  </div>
                  <div className="bg-white/10 rounded px-2 py-1">
                    <span className="text-xs font-bold text-white/60">AMEX</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
